import { useEffect, useRef, useState } from 'react';
import { apiFetch, ApiError } from './api';

// Stale-while-revalidate cache for GET endpoints.
//
// Why: the dashboard had no client cache, so every page mount refetched from a
// blank state — navigating away and back re-ran the (formerly multi-second)
// dashboard queries and flashed a loading spinner each time. This hook keeps
// the last successful payload in a module-level Map keyed by the FULL url
// (which already encodes the date window via `?from=…` and any other params),
// so remounting a view shows the previous data INSTANTLY and silently
// revalidates in the background.
//
// Scope: read-only GETs. The cached payloads here (Overview + its cards) are
// ingest-driven, never mutated by a dashboard user action, so there's no
// stale-after-write hazard. For endpoints a user CAN mutate, call
// invalidateCache() after the mutation (apiAction) before relying on this.

interface CacheEntry<T> {
  data: T;
  ts: number;
}

// Module-level: survives component unmount/remount (the whole point) and is
// shared across components that request the same url.
const cache = new Map<string, CacheEntry<unknown>>();
// In-flight dedupe: if two components mount the same url at once (or React
// StrictMode double-invokes the effect in dev), they share one request.
const inflight = new Map<string, Promise<unknown>>();

export interface CachedFetchOptions {
  /**
   * Silently revalidate when the tab becomes visible or the window regains
   * focus. Stale data stays on screen. Off by default so the rest of the
   * dashboard does not refetch on every focus.
   */
  refetchOnFocus?: boolean;
}

export interface CachedFetchResult<T> {
  /** Last known data — from cache instantly on remount, or null on a cold load. */
  data: T | null;
  /** Last error from the most recent revalidation (stale `data` is kept alongside). */
  error: Error | null;
  /** True only on a COLD load (nothing cached yet). A background revalidate does not flip this. */
  isLoading: boolean;
  /** True while a background revalidation is in flight (data already shown). */
  isValidating: boolean;
  /** Epoch ms of the last successful response for this url, or null before that. */
  fetchedAt: number | null;
  /** Force-drop the cached entry for this url and refetch. */
  refresh: () => void;
}

function revalidate<T>(url: string): Promise<T> {
  // Coalesce concurrent requests for the same url into one network call.
  const existing = inflight.get(url);
  if (existing) return existing as Promise<T>;
  const p = apiFetch<T>(url)
    .then((data) => {
      cache.set(url, { data, ts: Date.now() });
      return data;
    })
    .finally(() => {
      inflight.delete(url);
    });
  inflight.set(url, p);
  return p;
}

/**
 * Fetch `url` with stale-while-revalidate caching. Pass `null` to skip (e.g.
 * a conditional fetch); the hook then stays idle. Re-fetches whenever `url`
 * changes — so include every query param (window, filters) in the url and the
 * cache key falls out for free.
 */
export function useCachedFetch<T>(
  url: string | null,
  opts?: CachedFetchOptions,
): CachedFetchResult<T> {
  const refetchOnFocus = opts?.refetchOnFocus ?? false;
  const cached = url ? (cache.get(url) as CacheEntry<T> | undefined) : undefined;
  const [data, setData] = useState<T | null>(cached?.data ?? null);
  const [error, setError] = useState<Error | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number | null>(cached?.ts ?? null);
  // Cold load = we're about to fetch and have nothing cached to show.
  const [isLoading, setIsLoading] = useState<boolean>(!!url && !cached);
  // Identifies the latest requested url for this hook instance so a slow
  // response for a previous url can't overwrite newer state (race guard).
  const activeUrl = useRef<string | null>(url);

  useEffect(() => {
    activeUrl.current = url;
    if (!url) {
      setData(null);
      setFetchedAt(null);
      setError(null);
      setIsLoading(false);
      setIsValidating(false);
      return;
    }

    const hit = cache.get(url) as CacheEntry<T> | undefined;
    // Serve cached instantly; only show the cold spinner when there's nothing.
    setData(hit?.data ?? null);
    setFetchedAt(hit?.ts ?? null);
    setError(null);
    setIsLoading(!hit);
    setIsValidating(true);

    let cancelled = false;
    revalidate<T>(url)
      .then((fresh) => {
        if (cancelled || activeUrl.current !== url) return;
        setData(fresh);
        setFetchedAt((cache.get(url) as CacheEntry<T> | undefined)?.ts ?? Date.now());
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled || activeUrl.current !== url) return;
        // Keep the stale data visible; surface the error for the caller to
        // decide (most cards show stale data over an error card).
        setError(err instanceof ApiError || err instanceof Error ? err : new Error('Failed to load'));
      })
      .finally(() => {
        if (cancelled || activeUrl.current !== url) return;
        setIsLoading(false);
        setIsValidating(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Overview's inbound card opts in. A background revalidate keeps the
  // numbers moving when someone comes back to a tab that never unmounted.
  useEffect(() => {
    if (!url || !refetchOnFocus) return;
    let cancelled = false;
    let lastKick = 0;
    const kick = () => {
      if (cancelled || activeUrl.current !== url) return;
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastKick < 1000) return;
      lastKick = now;
      setIsValidating(true);
      revalidate<T>(url)
        .then((fresh) => {
          if (cancelled || activeUrl.current !== url) return;
          setData(fresh);
          setFetchedAt((cache.get(url) as CacheEntry<T> | undefined)?.ts ?? Date.now());
          setError(null);
        })
        .catch((err: unknown) => {
          if (cancelled || activeUrl.current !== url) return;
          setError(err instanceof ApiError || err instanceof Error ? err : new Error('Failed to load'));
        })
        .finally(() => {
          if (cancelled || activeUrl.current !== url) return;
          setIsLoading(false);
          setIsValidating(false);
        });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') kick();
    };
    window.addEventListener('focus', kick);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', kick);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [url, refetchOnFocus]);

  const refresh = () => {
    if (!url) return;
    cache.delete(url);
    setIsLoading(!cache.has(url));
    setIsValidating(true);
    revalidate<T>(url)
      .then((fresh) => {
        if (activeUrl.current === url) {
          setData(fresh);
          setFetchedAt((cache.get(url) as CacheEntry<T> | undefined)?.ts ?? Date.now());
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (activeUrl.current === url) {
          setError(err instanceof Error ? err : new Error('Failed to load'));
        }
      })
      .finally(() => {
        if (activeUrl.current === url) {
          setIsLoading(false);
          setIsValidating(false);
        }
      });
  };

  return { data, error, isLoading, isValidating, fetchedAt, refresh };
}

/**
 * Drop cached entries. With no argument, clears everything (e.g. on logout).
 * With a prefix, drops only matching urls — call after a mutation (apiAction)
 * that changes data served through useCachedFetch, e.g.
 * `invalidateCache('/api/orgs')`.
 */
export function invalidateCache(prefix?: string): void {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const key of [...cache.keys()]) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}
